'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useCookieConsent } from './cookie-consent'

const ADSENSE_CLIENT = process.env.NEXT_PUBLIC_ADSENSE_CLIENT || ''

// Interaction events that signal a real user is present. AdSense (and the
// Funding Choices consent bundle it auto-injects) only loads after one of
// these fires, so its ~310KB evaluation never lands inside the page-load
// critical path that Lighthouse measures for TBT (TES-858).
const INTERACTION_EVENTS = [
  'scroll',
  'pointerdown',
  'keydown',
  'touchstart',
  'mousemove',
] as const

/**
 * Pages with no placed slot never pull the AdSense script. Once it is on the
 * page it can inject its own zero-size `ins.adsbygoogle` anywhere, which is how
 * /guide ended up with a 0x0 container and no slot of ours. A placed slot
 * renders a `.tls-ad-band`; no band, no script.
 */
const SLOT_SELECTOR = '.tls-ad-band'

/**
 * AdSense's documented opt-out for the Ad intents format (links, anchors and the
 * "Discover more" chips): `google-anno-skip` on `<body>` keeps all three off the
 * whole page. Set before the script loads so the format never scans the page.
 * https://support.google.com/adsense/answer/13844047
 */
const AD_INTENTS_OPT_OUT_CLASS = 'google-anno-skip'

/**
 * Survives client-side navigation and consent changes. The interaction events
 * are one-shot, so a band that mounts after the first one (a soft navigation
 * to a slot page, or consent granted by the click that counts as the
 * interaction) has to find out the visitor is already here.
 */
let visitorInteracted = false

export function AdSenseLoader() {
  const { marketingAllowed } = useCookieConsent()
  const pathname = usePathname()

  useEffect(() => {
    if (!marketingAllowed || !ADSENSE_CLIENT) return

    let slotWatcher: MutationObserver | null = null
    let idleHandle: number | null = null

    const inject = () => {
      if (document.querySelector('script[src*="pagead2.googlesyndication.com"]')) return
      document.body.classList.add(AD_INTENTS_OPT_OUT_CLASS)
      const script = document.createElement('script')
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`
      script.async = true
      script.crossOrigin = 'anonymous'
      // Recorded on the element so a slot that mounts after the fact (a
      // client-side navigation) can still tell a blocked script from a slow one.
      script.addEventListener('load', () => { script.dataset.tlsState = 'loaded' }, { once: true })
      script.addEventListener('error', () => { script.dataset.tlsState = 'error' }, { once: true })
      document.head.appendChild(script)
    }

    // The visitor is here; load the script as soon as a band exists, now or
    // when one mounts later.
    const injectWhenSlotExists = () => {
      if (document.querySelector(SLOT_SELECTOR)) {
        inject()
        return
      }
      slotWatcher = new MutationObserver(() => {
        if (!document.querySelector(SLOT_SELECTOR)) return
        slotWatcher?.disconnect()
        slotWatcher = null
        inject()
      })
      slotWatcher.observe(document.body, { childList: true, subtree: true })
    }

    // On first interaction, hand off to requestIdleCallback so the heavy
    // AdSense evaluation still slots into an idle gap rather than competing
    // with the interaction it was triggered by.
    const onInteraction = () => {
      visitorInteracted = true
      cleanupListeners()
      if (typeof requestIdleCallback !== 'undefined') {
        idleHandle = requestIdleCallback(injectWhenSlotExists, { timeout: 2000 })
      } else {
        setTimeout(injectWhenSlotExists, 0)
      }
    }

    // No blind time fallback: AdSense loads ONLY on a genuine interaction
    // (scroll/pointer/key/touch/mousemove). Real users produce one within
    // ~1-2s so ad revenue is unaffected, but a synthetic agent that never
    // interacts (Lighthouse, crawlers) never triggers Google's DoubleClick
    // `test_cookie`. That cookie was the sole cause of the failing
    // `third-party-cookies` and `inspector-issues` Best Practices audits on
    // every page (TES-840). Extends the existing interaction-gating
    // philosophy from TES-858.
    function cleanupListeners() {
      for (const event of INTERACTION_EVENTS) {
        window.removeEventListener(event, onInteraction)
      }
    }

    if (visitorInteracted) {
      injectWhenSlotExists()
    } else {
      const addOpts: AddEventListenerOptions = { once: true, passive: true }
      for (const event of INTERACTION_EVENTS) {
        window.addEventListener(event, onInteraction, addOpts)
      }
    }

    return () => {
      cleanupListeners()
      slotWatcher?.disconnect()
      if (idleHandle !== null && typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(idleHandle)
      }
    }
  }, [marketingAllowed, pathname])

  return null
}
